import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AirspaceProperties, NotamBulletin, Position } from '@jupi/shared'
import { api } from '@jupi/ui'
import { AIRSPACE_URL } from '../pages/plan/mapStyle/airspaceLayers'

export const notamKeys = {
  efin: ['notams', 'efin'] as const,
  airspace: ['notams', 'airspace'] as const,
}

export function useEfinNotams({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: notamKeys.efin,
    queryFn: () => api.get<NotamBulletin>('/api/notams/efin'),
    // The API refreshes every 15 minutes; NOF publishes about hourly.
    refetchInterval: 5 * 60_000,
    staleTime: 2 * 60_000,
    enabled,
  })
}

type AirspaceCollection = GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon, AirspaceProperties>

/**
 * Outer rings of the named eAIP areas, from the static airspace.geojson the
 * map draws (the browser's copy is shared with it). Fetched only when some
 * NOTAM names an area.
 */
export function useAirspaceRings(ids: string[]): ReadonlyMap<string, Position[][]> | undefined {
  const { data } = useQuery({
    queryKey: notamKeys.airspace,
    queryFn: async () => {
      const res = await fetch(AIRSPACE_URL)
      if (!res.ok) throw new Error(`airspace.geojson: HTTP ${res.status}`)
      return (await res.json()) as AirspaceCollection
    },
    staleTime: Infinity,
    enabled: ids.length > 0,
  })
  const key = ids.join(' ')
  return useMemo(() => {
    if (!data) return undefined
    const wanted = new Set(key.split(' '))
    const rings = new Map<string, Position[][]>()
    for (const f of data.features) {
      if (!wanted.has(f.properties.id)) continue
      const polygons = f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates
      rings.set(f.properties.id, polygons.map((p) => p[0] as Position[]))
    }
    return rings
  }, [data, key])
}
